/**
 * Widget catalog injection seam (platform-agnostic).
 *
 * Apps register design-system-specific widget renderers — e.g. the Duro
 * primitives (button/heading/card) from builder-webapp — into this registry, and
 * the editor's catalog resolves a registered renderer *before* its built-in one.
 * So the editor never imports a design system: it stays a generic engine and the
 * app supplies the concrete widgets. This is also the Phase-4 "bring your own
 * components" mechanism.
 *
 * Web + native share this module (no `.native` variant), so each app's bundle
 * has one registry; the web app registers Duro-web renderers and a native app
 * registers native ones, while the editor's dispatch is identical on both.
 */
import type { ComponentType } from "react";

import type { WidgetComponent } from "./types.js";

/** A widget's settings form: the app's controls for one item's props. */
export type WidgetSettingsComponent = ComponentType<{
  readonly gridId: string;
  readonly itemId: string;
}>;

const extensions: Record<string, WidgetComponent> = {};
const settings: Record<string, WidgetSettingsComponent> = {};

/** Register (or override) the renderer for a widget contentType. */
export function registerWidget(
  contentType: string,
  component: WidgetComponent,
): void {
  extensions[contentType] = component;
}

/** The app-registered renderer for a contentType, if any. */
export function registeredWidget(
  contentType: string,
): WidgetComponent | undefined {
  return extensions[contentType];
}

/** Register (or override) the settings form for a widget contentType. The
 *  editor knows no design system's prop vocabulary, so the app supplies the
 *  form the same way it supplies the renderer. */
export function registerWidgetSettings(
  contentType: string,
  component: WidgetSettingsComponent,
): void {
  settings[contentType] = component;
}

/** The app-registered settings form for a contentType, if any. */
export function registeredWidgetSettings(
  contentType: string,
): WidgetSettingsComponent | undefined {
  return settings[contentType];
}

const quickEdits: Record<string, WidgetSettingsComponent> = {};

/** Register the quick-edit card for a widget contentType: its one to three
 *  most used fields, shown in a card attached to the widget on the canvas.
 *  "All settings" (the full form) stays the place for everything else. */
export function registerWidgetQuickEdit(
  contentType: string,
  component: WidgetSettingsComponent,
): void {
  quickEdits[contentType] = component;
}

/** The app-registered quick-edit card for a contentType, if any. */
export function registeredWidgetQuickEdit(
  contentType: string,
): WidgetSettingsComponent | undefined {
  return quickEdits[contentType];
}
